import { createApp, createSSRApp, nextTick } from 'vue';
import store from 'scripts/model/model';
import components from 'scripts/components';
import directives from 'scripts/directives';
import {loader} from 'scripts/loader';

const HYDRATABLE_ATTR = 'data-hydratable';
const TEMPLATE_ATTR = 'data-v-template';

const isHydratableElement = (el) => {
	return Boolean(
		el &&
		el.hasAttribute(HYDRATABLE_ATTR)
	);
};

const encodeTemplate = (template) => {
	return encodeURIComponent(template || '');
};

const decodeTemplate = (template) => {
	try {
		return decodeURIComponent(template || '');
	} catch (error) {
		console.error('Cannot decode Vue template:', error);

		return '';
	}
};

const saveTemplateInElement = (el, template) => {
	if (!el || !template) {
		return;
	}

	el.setAttribute(TEMPLATE_ATTR, encodeTemplate(template));
};

const getStoredTemplate = (el) => {
	const template = el?.getAttribute(TEMPLATE_ATTR);

	if (!template) {
		return '';
	}

	return decodeTemplate(template).trim();
};

const unwrapElement = (el) => {
	if (!el || !el.parentNode) {
		return;
	}

	const parent = el.parentNode;

	while (el.firstChild) {
		parent.insertBefore(el.firstChild, el);
	}

	parent.removeChild(el);
};

const installAppParts = (app) => {
	app.config.globalProperties.$log = console.log;

	app.use(store);

	for (let name in components) {
		app.component(name, components[name]);
	}

	for (let name in directives) {
		app.directive(name, directives[name]);
	}
};

const createLegacyAppExt = (el) => {
	const app = createApp({
		mounted() {
			this.$el.parentNode.replaceWith(this.$el);
		}
	});

	installAppParts(app);

	if (el) {
		app.mount(el);
	}

	return app;
};

const createHydratableAppExt = (el, options = {}) => {
	const hydratableSnapshot = options.hydratableSnapshot ?? false;
	const hydrate = options.hydrate ?? isHydratableElement(el);
	const unwrapAfterHydrate = options.unwrapAfterHydrate ?? false;

	const initialTemplate = !hydrate && el ? el.innerHTML.trim() : '';

	const storedTemplate = hydrate ? getStoredTemplate(el) : '';
	const template = hydrate ? storedTemplate : initialTemplate;

	const rootComponent = {
		template,

		mounted() {
			if (!hydrate && !hydratableSnapshot) {
				this.$el.parentNode.replaceWith(this.$el);
			}

			if (!hydrate && hydratableSnapshot && el) {
				el.setAttribute(HYDRATABLE_ATTR, '');

				saveTemplateInElement(el, initialTemplate);
			}

			if (hydrate && unwrapAfterHydrate) {
				nextTick(() => {
					unwrapElement(el);
				});
			}
		}
	};

	if (!rootComponent.template) {
		console.warn('Vue scope has no template:', el);
	}

	const app = (hydrate ? createSSRApp : createApp)(rootComponent);

	installAppParts(app);

	if (el) {
		app.mount(el);
	}

	return app;
};

const createAppExt = (el, options) => {
	const isOptionsObject = options && typeof options === 'object';

	if (!isOptionsObject) {
		return createLegacyAppExt(el);
	}

	return createHydratableAppExt(el, options);
};

export { createAppExt, loader };
